import React, { useRef, useState, useEffect } from 'react';
import '@fortawesome/fontawesome-free/css/all.min.css';
import './PictureUploadBox.css';

interface PictureUploadBoxProps {
  onUpload: (image: File | undefined) => void; // Updated to accept undefined
  label?: string;
  selectedImage?: string; // New prop for existing image file
}

const PictureUploadBox: React.FC<PictureUploadBoxProps> = ({ onUpload, label, selectedImage }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | null>(null);

  useEffect(() => {
    // Update the preview when selectedImage changes
    if (selectedImage) {
      setPreview(selectedImage);
    } else {
      setPreview(null);
    }
  }, [selectedImage]);

  const handleClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    
    // Validate file type to ensure it's an image
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result as string);
        onUpload(file);
      };
      reader.readAsDataURL(file);
    } else {
      alert('Please upload a valid image file (jpg, png, etc.)'.toTranslatedDisplay());
    }
  };

  const handleDelete = (event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent triggering the upload click
    setPreview(null);
    onUpload(undefined); // Call back with undefined
    
    // Clear the file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="upload-box" onClick={handleClick}>
      {preview ? (
        <div className="image-container">
          <img src={preview} alt="Preview" className="upload-preview" />
          <button 
            className="delete-button"
            onClick={handleDelete}
            type="button"
            aria-label={'Delete image'.toTranslatedDisplay()}
          >
            <i className="fas fa-times"></i>
          </button>
        </div>
      ) : (
        <>
          <i className="fas fa-cloud-upload-alt upload-icon me-2"></i>
          {label ? label : 'Click to upload image'.toTranslatedDisplay()}
        </>
      )}
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        accept="image/*"  
        onChange={handleFileChange}
      />
    </div>
  );
};

export default PictureUploadBox;